import { DEFAULT_AD_CTA } from './ad-subjects';
import { adLanguageBase, defaultAdCta, localizedSubject, type AdLanguageBase, type AdLanguageId } from './ad-language';
import type { AdSubject, LegalLine, LegalPreset } from './banner-kit-api';
import type { JsonObject, PlayableConfigFill, PlayableCopy, PlayableParams } from './playable-api';

/**
 * A mini-game's on-screen text is never the owner's: a game is a real-money gambling ad, and free text would let it
 * promise what the promotion does not offer. Each game reads a FIXED, vetted set of lines (what to do, never what is
 * won), in the ad's language (the document's `language`; an older document without one reads in the language its
 * promotion's copy is written in). The call to action is the promotion's own; the offer itself is locked to the
 * promotion elsewhere (`withLockedOffer`).
 *
 * The lines are AD CONTENT in the ad's language, not Studio UI copy: they never go through i18n (the owner's Studio
 * language must not change what the ad says) and the Studio shows them as data.
 */

/** The languages an older document's promotion copy is told apart in ({@link detectAdLanguage}). */
export type AdLanguage = 'en' | 'es' | 'pt' | 'fr';

/** The detected languages in tie-break order (the first wins a tie; English when nothing matches). */
export const DETECTED_LANGUAGES: readonly AdLanguage[] = ['en', 'es', 'pt', 'fr'];

/** The languages a mini-game's fixed text is written in: every base language an ad can be in. */
export const AD_LANGUAGES: readonly AdLanguageBase[] = ['en', 'es', 'pt', 'fr', 'af', 'ro', 'hu'];

/** One game's fixed lines. Slots (the runtime's, mirrored by the backend's config-fill limits): `headline` at most
 *  40 characters (the top line), `instruction` 24 (the footer), `reveal_label` 24 (above the offer on the end card),
 *  `unlocked_label` 40 (Bonus Meter only: the full meter, its `config.unlocked_label`). */
export interface GameText {
  headline: string;
  instruction: string;
  reveal_label: string;
  unlocked_label?: string;
}

/** The slot limits of {@link GameText} (and the call to action's), in characters. */
export const GAME_TEXT_LIMITS = {
  headline: 40,
  instruction: 24,
  reveal_label: 24,
  unlocked_label: 40,
  cta: 16,
} as const;

/** The reveal label every game shares: the line above the promotion's offer. */
const REVEAL: Record<AdLanguageBase, string> = {
  en: 'Your offer',
  es: 'Tu oferta',
  pt: 'Sua oferta',
  fr: 'Votre offre',
  af: 'Jou aanbod',
  ro: 'Oferta ta',
  hu: 'Az ajánlatod',
};

/** A game's lines per language from its `[headline, instruction]` pairs (and the Bonus Meter's full-meter label). */
const game = (
  lines: Record<AdLanguageBase, [string, string]>,
  unlocked?: Record<AdLanguageBase, string>,
): Record<AdLanguageBase, GameText> => {
  const out = {} as Record<AdLanguageBase, GameText>;
  AD_LANGUAGES.forEach((lang) => {
    const [headline, instruction] = lines[lang];
    out[lang] = {
      headline,
      instruction,
      reveal_label: REVEAL[lang],
      ...(unlocked ? { unlocked_label: unlocked[lang] } : {}),
    };
  });
  return out;
};

/**
 * The fixed text per game template id: what the player does and that the game shows the offer. Never a win, a prize,
 * "free", a guarantee or urgency. Spanish is neutral Latin American, Portuguese Brazilian, French Canadian.
 */
export const MINI_GAME_TEXT: Readonly<Record<string, Record<AdLanguageBase, GameText>>> = {
  'offer-wheel': game({
    en: ['Spin the wheel to see the offer', 'Tap to spin'],
    es: ['Gira la rueda para ver la oferta', 'Toca para girar'],
    pt: ['Gire a roda para ver a oferta', 'Toque para girar'],
    fr: ["Tournez la roue pour voir l'offre", 'Touchez pour tourner'],
    af: ['Draai die wiel om die aanbod te sien', 'Tik om te draai'],
    ro: ['Învârte roata pentru a vedea oferta', 'Atinge pentru a învârti'],
    hu: ['Pörgess, és nézd meg az ajánlatot', 'Koppints a pörgetéshez'],
  }),
  'scratch-reveal': game({
    en: ['Scratch to see the offer', 'Scratch here'],
    es: ['Raspa para ver la oferta', 'Raspa aquí'],
    pt: ['Raspe para ver a oferta', 'Raspe aqui'],
    fr: ["Grattez pour voir l'offre", 'Grattez ici'],
    af: ['Krap om die aanbod te sien', 'Krap hier'],
    ro: ['Răzuiește pentru a vedea oferta', 'Răzuiește aici'],
    hu: ['Kaparj, és nézd meg az ajánlatot', 'Kaparj itt'],
  }),
  'pick-a-gift': game({
    en: ['Pick one to see the offer', 'Tap one to open'],
    es: ['Elige uno para ver la oferta', 'Toca uno para abrir'],
    pt: ['Escolha um para ver a oferta', 'Toque em um para abrir'],
    fr: ["Choisissez-en un pour voir l'offre", 'Touchez pour ouvrir'],
    af: ['Kies een om die aanbod te sien', 'Tik een om oop te maak'],
    ro: ['Alege unul pentru a vedea oferta', 'Atinge pentru a deschide'],
    hu: ['Válassz egyet, és nézd meg az ajánlatot', 'Koppints az egyikre'],
  }),
  'symbol-match': game({
    en: ['Match the pairs to see the offer', 'Find the pairs'],
    es: ['Junta los pares para ver la oferta', 'Encuentra los pares'],
    pt: ['Combine os pares para ver a oferta', 'Encontre os pares'],
    fr: ["Associez les paires pour voir l'offre", 'Trouvez les paires'],
    af: ['Koppel die pare om die aanbod te sien', 'Vind die pare'],
    ro: ['Formează perechile pentru a vedea oferta', 'Găsește perechile'],
    hu: ['Párosíts, és nézd meg az ajánlatot', 'Keresd a párokat'],
  }),
  'vault-crack': game({
    en: ['Open the vault to see the offer', 'Tap to open'],
    es: ['Abre la bóveda para ver la oferta', 'Toca para abrir'],
    pt: ['Abra o cofre para ver a oferta', 'Toque para abrir'],
    fr: ["Ouvrez le coffre pour voir l'offre", 'Touchez pour ouvrir'],
    af: ['Maak die kluis oop en sien die aanbod', 'Tik om oop te maak'],
    ro: ['Deschide seiful pentru a vedea oferta', 'Atinge pentru a deschide'],
    hu: ['Nyisd ki a széfet, benne az ajánlat', 'Koppints a nyitáshoz'],
  }),
  'bonus-meter': game(
    {
      en: ['Fill the meter to see the offer', 'Tap to collect'],
      es: ['Llena el medidor para ver la oferta', 'Toca para recolectar'],
      pt: ['Encha o medidor para ver a oferta', 'Toque para coletar'],
      fr: ["Remplissez la jauge pour voir l'offre", 'Touchez pour ramasser'],
      af: ['Vul die meter om die aanbod te sien', 'Tik om te versamel'],
      ro: ['Umple indicatorul pentru a vedea oferta', 'Atinge pentru a colecta'],
      hu: ['Töltsd fel a mérőt, és jön az ajánlat', 'Koppints a gyűjtéshez'],
    },
    {
      en: 'Meter full',
      es: 'Medidor lleno',
      pt: 'Medidor cheio',
      fr: 'Jauge pleine',
      af: 'Meter vol',
      ro: 'Indicator plin',
      hu: 'Megtelt a mérő',
    },
  ),
  'golden-ticket': game({
    en: ['Open the ticket to see the offer', 'Drag to open'],
    es: ['Abre el boleto para ver la oferta', 'Arrastra para abrir'],
    pt: ['Abra o bilhete para ver a oferta', 'Arraste para abrir'],
    fr: ["Ouvrez le billet pour voir l'offre", 'Glissez pour ouvrir'],
    af: ['Maak die kaartjie oop en sien die aanbod', 'Sleep om oop te maak'],
    ro: ['Deschide biletul pentru a vedea oferta', 'Trage pentru a deschide'],
    hu: ['Nyisd ki a jegyet, benne az ajánlat', 'Húzd a nyitáshoz'],
  }),
  'lucky-drop': game({
    en: ['Drop the gem to see the offer', 'Tap to drop'],
    es: ['Suelta la gema para ver la oferta', 'Toca para soltar'],
    pt: ['Solte a joia para ver a oferta', 'Toque para soltar'],
    fr: ["Lâchez la gemme pour voir l'offre", 'Touchez pour lâcher'],
    af: ['Laat die juweel val en sien die aanbod', 'Tik om te laat val'],
    ro: ['Dă drumul gemei pentru a vedea oferta', 'Atinge ca să cadă'],
    hu: ['Ejtsd le a drágakövet, és jön az ajánlat', 'Koppints az ejtéshez'],
  }),
  'high-striker': game({
    en: ['Ring the bell to see the offer', 'Tap to strike'],
    es: ['Toca la campana para ver la oferta', 'Toca para golpear'],
    pt: ['Toque o sino para ver a oferta', 'Toque para golpear'],
    fr: ["Sonnez la cloche pour voir l'offre", 'Touchez pour frapper'],
    af: ['Lui die klok om die aanbod te sien', 'Tik om te slaan'],
    ro: ['Sună clopotul pentru a vedea oferta', 'Atinge pentru a lovi'],
    hu: ['Üsd meg a harangot, és jön az ajánlat', 'Koppints az ütéshez'],
  }),
};

/** The text of a game with no fixed set of its own (none chosen yet, or a template newer than this table). */
export const GENERIC_GAME_TEXT: Record<AdLanguageBase, GameText> = game({
  en: ['Play to see the offer', 'Tap to play'],
  es: ['Juega para ver la oferta', 'Toca para jugar'],
  pt: ['Jogue para ver a oferta', 'Toque para jogar'],
  fr: ["Jouez pour voir l'offre", 'Touchez pour jouer'],
  af: ['Speel om die aanbod te sien', 'Tik om te speel'],
  ro: ['Joacă pentru a vedea oferta', 'Atinge pentru a juca'],
  hu: ['Játssz, és nézd meg az ajánlatot', 'Koppints a játékhoz'],
});

/* ---------- the ad's language ---------- */

/** Words that tell a language apart in short promotion copy (function words and the usual offer words). A word
 *  shared by two languages is listed under both, so it never tips one over the other. */
const STOP_WORDS: Record<AdLanguage, ReadonlySet<string>> = {
  en: new Set([
    'the',
    'and',
    'your',
    'you',
    'up',
    'to',
    'of',
    'on',
    'for',
    'with',
    'get',
    'first',
    'deposit',
    'spins',
    'play',
    'claim',
    'welcome',
    'join',
    'weekly',
    'every',
    'now',
    'match',
    'offer',
    'reload',
    'tournament',
    'week',
    'free',
    'plus',
  ]),
  es: new Set([
    'el',
    'la',
    'los',
    'las',
    'del',
    'y',
    'en',
    'tu',
    'tus',
    'con',
    'para',
    'por',
    'un',
    'una',
    'hasta',
    'primer',
    'depósito',
    'deposito',
    'giros',
    'gratis',
    'bono',
    'bienvenida',
    'juega',
    'reclamar',
    'reclama',
    'semanal',
    'únete',
    'ahora',
    'más',
    'al',
    'oferta',
    'recarga',
    'torneo',
    'apuesta',
    'obtén',
  ]),
  pt: new Set([
    'o',
    'os',
    'do',
    'da',
    'dos',
    'das',
    'e',
    'seu',
    'sua',
    'com',
    'para',
    'por',
    'um',
    'uma',
    'até',
    'primeiro',
    'depósito',
    'deposito',
    'rodadas',
    'giros',
    'grátis',
    'gratis',
    'bônus',
    'boas',
    'vindas',
    'jogue',
    'resgatar',
    'resgate',
    'semanal',
    'agora',
    'mais',
    'na',
    'oferta',
    'você',
    'receba',
    'torneio',
    'aposte',
    'ganhe',
  ]),
  fr: new Set([
    'le',
    'la',
    'les',
    'des',
    'du',
    'et',
    'en',
    'un',
    'une',
    'votre',
    'vos',
    'avec',
    'pour',
    'par',
    'jusqu',
    'premier',
    'dépôt',
    'tours',
    'gratuits',
    'gratuit',
    'bienvenue',
    'jouez',
    'réclamer',
    'réclamez',
    'obtenez',
    'hebdomadaire',
    'maintenant',
    'plus',
    'au',
    'aux',
    'offre',
    'recharge',
    'tournoi',
    'pariez',
  ]),
};

/** Letters only one or two of the languages write: each adds `weight` to every language listed. */
const LETTER_HINTS: ReadonlyArray<{ re: RegExp; langs: AdLanguage[]; weight: number }> = [
  { re: /[ñ¿¡]/g, langs: ['es'], weight: 2 },
  { re: /[ãõ]/g, langs: ['pt'], weight: 2 },
  { re: /[èùœëïîûâ]/g, langs: ['fr'], weight: 2 },
  { re: /[çê]/g, langs: ['pt', 'fr'], weight: 1 },
];

/**
 * The language of a promotion's copy (its headline, subtitle and call to action): each language scores one point per
 * word of its own ({@link STOP_WORDS}) and a little per letter only it writes; the highest score wins, a tie goes to
 * the language listed first in {@link DETECTED_LANGUAGES}, and English when nothing matches. Only an older document
 * (no `language`) is read this way. Deterministic. Pure.
 */
export const detectAdLanguage = (subject: Pick<AdSubject, 'headline' | 'subtitle' | 'cta_text'>): AdLanguage => {
  const text = [subject.headline, subject.subtitle, subject.cta_text].filter(Boolean).join(' ').toLowerCase();
  const score: Record<AdLanguage, number> = { en: 0, es: 0, pt: 0, fr: 0 };
  for (const word of text.split(/[^a-zà-öø-ÿœ]+/)) {
    if (!word) continue;
    DETECTED_LANGUAGES.forEach((lang) => {
      if (STOP_WORDS[lang].has(word)) score[lang] += 1;
    });
  }
  LETTER_HINTS.forEach(({ re, langs, weight }) => {
    const hits = text.match(re)?.length ?? 0;
    langs.forEach((lang) => {
      score[lang] += hits * weight;
    });
  });
  return DETECTED_LANGUAGES.reduce<AdLanguage>((best, lang) => (score[lang] > score[best] ? lang : best), 'en');
};

/* ---------- the locked text ---------- */

/** A game's fixed lines in a language (the generic set for a template with none of its own). Pure. */
export const gameTextFor = (templateId: string, lang: AdLanguageBase): GameText =>
  (MINI_GAME_TEXT[templateId] ?? GENERIC_GAME_TEXT)[lang];

/** The language a mini-game's fixed lines are written in: the ad's own (its base language), else (an older document
 *  with none) the language its promotion's copy reads in ({@link detectAdLanguage}). Pure. */
export const gameTextLanguage = (
  subject: Pick<AdSubject, 'headline' | 'subtitle' | 'cta_text'>,
  language?: AdLanguageId,
): AdLanguageBase => ((language ?? '').trim() ? adLanguageBase(language) : detectAdLanguage(subject));

/** A mini-game's locked text: the on-screen copy and the template settings that are text on screen. */
export interface MiniGameCopy {
  copy: PlayableCopy;
  /** Config fields the lock owns (Bonus Meter's `unlocked_label`); empty for the other games. */
  config: JsonObject;
}

/**
 * The text a mini-game of `templateId` advertising `subject` in `language` shows: the game's fixed lines in that
 * language ({@link gameTextLanguage}), and the promotion's own call to action (else the promotion default, in that
 * language). `subject` is the promotion as the ad shows it (its copy in the ad's language, see `localizedSubject`).
 * Pure.
 */
export const gameCopyFor = (
  templateId: string,
  subject: Pick<AdSubject, 'headline' | 'subtitle' | 'cta_text'>,
  language?: AdLanguageId,
): MiniGameCopy => {
  const text = gameTextFor(templateId, gameTextLanguage(subject, language));
  const fallback = (language ?? '').trim() ? defaultAdCta('promotion', language) : DEFAULT_AD_CTA.promotion;
  return {
    copy: {
      headline: text.headline,
      instruction: text.instruction,
      reveal_label: text.reveal_label,
      cta: (subject.cta_text ?? '').trim() || fallback,
    },
    config: text.unlocked_label ? { unlocked_label: text.unlocked_label } : {},
  };
};

/** The market a mini-game's legal line falls back to when it holds the kits' Custom choice. */
export const MINI_GAME_LEGAL_PRESET: Exclude<LegalPreset, 'custom'> = 'uk_eu';

/** A mini-game's legal line: always on, always its market preset's own text (a Custom line falls back to
 *  {@link MINI_GAME_LEGAL_PRESET}: the runtime draws nothing for Custom without text), written in the ad's language
 *  where the line is drawn (the runtime and the packager read the document's `language`). Pure. */
export const miniGameLegal = (legal: LegalLine): LegalLine => ({
  enabled: true,
  preset: legal.preset === 'custom' ? MINI_GAME_LEGAL_PRESET : legal.preset,
  text: '',
});

/** The promotion as a mini-game shows it: its copy in the ad's language when the document holds it
 *  (`localized_copy`), its call to action the promotion default in that language when it has none. An older document
 *  (no language) shows the promotion as it was snapshotted. Pure. */
export const miniGameSubject = (doc: PlayableParams): AdSubject =>
  localizedSubject(doc.subject, doc.localized_copy, doc.language, 'promotion');

/** The mini-game document with its text locked: the game's fixed lines and the promotion's call to action in the
 *  ad's language ({@link gameCopyFor}) and the preset legal line ({@link miniGameLegal}). The offer is locked
 *  separately (`withLockedOffer`). Idempotent. Pure. */
export const withLockedGameText = (doc: PlayableParams): PlayableParams => {
  const locked = gameCopyFor(doc.template_id, miniGameSubject(doc), doc.language);
  return {
    ...doc,
    copy: locked.copy,
    config: Object.keys(locked.config).length > 0 ? { ...doc.config, ...locked.config } : doc.config,
    legal: miniGameLegal(doc.legal),
  };
};

/** A Lumio fill as a mini-game may take it: the game, its settings, colours, button and fonts, never text (the copy
 *  is locked, see {@link gameCopyFor}). Pure. */
export const miniGameFill = (fill: PlayableConfigFill): PlayableConfigFill => {
  const { copy: _copy, ...rest } = fill;
  return rest;
};
